/**
 * Canonical Metadata Schema
 * 
 * This defines the unified metadata model that all format-specific adapters
 * translate to/from. This avoids the "which field is authoritative?" problem
 * when EXIF ↔ IPTC ↔ XMP overlap.
 */

export interface CanonicalMetadata {
  // Core identification
  title?: string;
  description?: string;
  keywords?: string[];
  creator?: string;
  copyright?: string;
  
  // Geographic information
  gps?: {
    latitude?: number;
    longitude?: number;
    altitude?: number;
    location?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  
  // Rating and organization
  rating?: number; // 0-5 stars
  colorLabel?: 'Red' | 'Yellow' | 'Green' | 'Blue' | 'Purple';
  
  // People and subjects
  people?: string[];
  
  // Media-specific metadata
  album?: {
    title?: string;
    artist?: string;
    track?: number;
    totalTracks?: number;
    disc?: number;
    totalDiscs?: number;
    genre?: string;
    year?: number;
    bpm?: number;
    key?: string;
    mood?: string;
  };
  
  show?: {
    title?: string;
    season?: number;
    episode?: number;
    totalEpisodes?: number;
    network?: string;
    director?: string;
    writer?: string;
    cast?: string[];
  };
  
  // Artwork and media
  artwork?: {
    frontCover?: string; // Base64 or file path
    backCover?: string;
    thumbnail?: string;
    poster?: string;
  };
  
  // Technical metadata (read-only, from ffprobe)
  technical?: {
    duration?: number; // seconds
    bitrate?: number; // bps
    sampleRate?: number; // Hz
    channels?: number;
    codec?: string;
    container?: string;
    resolution?: {
      width: number;
      height: number;
    };
    frameRate?: number; // fps
    colorSpace?: string;
    colorDepth?: number; // bits
  };
  
  // Provenance and workflow
  provenance?: {
    created?: Date;
    modified?: Date;
    software?: string;
    version?: string;
    source?: string;
    workflow?: string[];
  };
  
  // Language and localization
  language?: string;
  locale?: string;
  
  // Custom fields
  custom?: Record<string, string | number | boolean | string[]>;
}

export interface MetadataAdapter {
  /**
   * Read metadata from file and convert to canonical format
   */
  read(file: File): Promise<CanonicalMetadata>;
  
  /**
   * Write canonical metadata to file
   */
  write(file: File, metadata: CanonicalMetadata): Promise<File>;
  
  /**
   * Get supported file extensions
   */
  getSupportedExtensions(): string[];
  
  /**
   * Check if adapter supports the given file type
   */
  supports(file: File): boolean;
}

export interface MetadataProvenance {
  source: string; // 'exif', 'iptc', 'xmp', 'id3', 'mp4', etc.
  confidence: number; // 0-1
  timestamp: Date;
  software?: string;
  version?: string;
}

export interface MetadataValidationResult {
  isValid: boolean;
  errors: Array<{
    field: string;
    message: string;
    severity: 'error' | 'warning' | 'info';
  }>;
  warnings: Array<{
    field: string;
    message: string;
  }>;
  suggestions: Array<{
    field: string;
    suggestion: string;
    reason: string;
  }>;
}

/**
 * Metadata field definitions with validation rules
 */
export interface MetadataFieldDefinition {
  key: keyof CanonicalMetadata;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object' | 'date';
  required?: boolean;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
  description?: string;
  category: 'basic' | 'geographic' | 'media' | 'technical' | 'workflow';
  priority: number; // 1-10, higher = more important
  editable: boolean;
  provenance?: MetadataProvenance[];
}

/**
 * Standard field definitions for the canonical schema
 */
export const CANONICAL_FIELDS: Record<keyof CanonicalMetadata, MetadataFieldDefinition> = {
  title: {
    key: 'title',
    label: 'Title',
    type: 'string',
    required: false,
    validation: { max: 255 },
    description: 'Main title of the media',
    category: 'basic',
    priority: 9,
    editable: true
  },
  description: {
    key: 'description',
    label: 'Description',
    type: 'string',
    required: false,
    validation: { max: 1000 },
    description: 'Detailed description of the content',
    category: 'basic',
    priority: 8,
    editable: true
  },
  keywords: {
    key: 'keywords',
    label: 'Keywords',
    type: 'array',
    required: false,
    description: 'Searchable keywords and tags',
    category: 'basic',
    priority: 7,
    editable: true
  },
  creator: {
    key: 'creator',
    label: 'Creator',
    type: 'string',
    required: false,
    validation: { max: 255 },
    description: 'Primary creator/author',
    category: 'basic',
    priority: 8,
    editable: true
  },
  copyright: {
    key: 'copyright',
    label: 'Copyright',
    type: 'string',
    required: false,
    validation: { max: 255 },
    description: 'Copyright information',
    category: 'basic',
    priority: 6,
    editable: true
  },
  gps: {
    key: 'gps',
    label: 'GPS Location',
    type: 'object',
    required: false,
    description: 'Geographic location information',
    category: 'geographic',
    priority: 5,
    editable: true
  },
  rating: {
    key: 'rating',
    label: 'Rating',
    type: 'number',
    required: false,
    validation: { min: 0, max: 5 },
    description: 'Quality rating (0-5 stars)',
    category: 'basic',
    priority: 6,
    editable: true
  },
  colorLabel: {
    key: 'colorLabel',
    label: 'Color Label',
    type: 'string',
    required: false,
    description: 'Color-coded organization label',
    category: 'basic',
    priority: 4,
    editable: true
  },
  people: {
    key: 'people',
    label: 'People',
    type: 'array',
    required: false,
    description: 'People featured in the media',
    category: 'basic',
    priority: 5,
    editable: true
  },
  album: {
    key: 'album',
    label: 'Album Information',
    type: 'object',
    required: false,
    description: 'Album and track information',
    category: 'media',
    priority: 8,
    editable: true
  },
  show: {
    key: 'show',
    label: 'Show Information',
    type: 'object',
    required: false,
    description: 'TV show and episode information',
    category: 'media',
    priority: 8,
    editable: true
  },
  artwork: {
    key: 'artwork',
    label: 'Artwork',
    type: 'object',
    required: false,
    description: 'Cover art and thumbnails',
    category: 'media',
    priority: 7,
    editable: true
  },
  technical: {
    key: 'technical',
    label: 'Technical Information',
    type: 'object',
    required: false,
    description: 'Technical metadata (read-only)',
    category: 'technical',
    priority: 3,
    editable: false
  },
  provenance: {
    key: 'provenance',
    label: 'Provenance',
    type: 'object',
    required: false,
    description: 'Creation and modification history',
    category: 'workflow',
    priority: 2,
    editable: false
  },
  language: {
    key: 'language',
    label: 'Language',
    type: 'string',
    required: false,
    validation: { pattern: '^[a-z]{2}(-[A-Z]{2})?$' },
    description: 'Content language (ISO 639-1)',
    category: 'basic',
    priority: 4,
    editable: true
  },
  locale: {
    key: 'locale',
    label: 'Locale',
    type: 'string',
    required: false,
    validation: { pattern: '^[a-z]{2}_[A-Z]{2}$' },
    description: 'Regional locale (e.g., en_US)',
    category: 'basic',
    priority: 3,
    editable: true
  },
  custom: {
    key: 'custom',
    label: 'Custom Fields',
    type: 'object',
    required: false,
    description: 'User-defined custom fields',
    category: 'basic',
    priority: 1,
    editable: true
  }
};

/**
 * Validate canonical metadata against field definitions
 */
export function validateCanonicalMetadata(metadata: CanonicalMetadata): MetadataValidationResult {
  const errors: Array<{ field: string; message: string; severity: 'error' | 'warning' | 'info' }> = [];
  const warnings: Array<{ field: string; message: string }> = [];
  const suggestions: Array<{ field: string; suggestion: string; reason: string }> = [];

  Object.entries(CANONICAL_FIELDS).forEach(([key, definition]) => {
    const value = metadata[key as keyof CanonicalMetadata];
    
    if (definition.required && (value === undefined || value === null || value === '')) {
      errors.push({
        field: key,
        message: `${definition.label} is required`,
        severity: 'error'
      });
    }

    if (value !== undefined && value !== null && definition.validation) {
      const { min, max, pattern, message } = definition.validation;
      
      if (typeof value === 'string') {
        if (min !== undefined && value.length < min) {
          errors.push({
            field: key,
            message: message || `${definition.label} must be at least ${min} characters`,
            severity: 'error'
          });
        }
        if (max !== undefined && value.length > max) {
          errors.push({
            field: key,
            message: message || `${definition.label} must be no more than ${max} characters`,
            severity: 'error'
          });
        }
        if (pattern && !new RegExp(pattern).test(value)) {
          errors.push({
            field: key,
            message: message || `${definition.label} format is invalid`,
            severity: 'error'
          });
        }
      }
      
      if (typeof value === 'number') {
        if (min !== undefined && value < min) {
          errors.push({
            field: key,
            message: message || `${definition.label} must be at least ${min}`,
            severity: 'error'
          });
        }
        if (max !== undefined && value > max) {
          errors.push({
            field: key,
            message: message || `${definition.label} must be no more than ${max}`,
            severity: 'error'
          });
        }
      }
    }

    // Generate suggestions
    if (key === 'title' && !value && metadata.description) {
      suggestions.push({
        field: key,
        suggestion: 'Consider adding a title based on the description',
        reason: 'Title helps with organization and searchability'
      });
    }
    
    if (key === 'keywords' && !value && metadata.description) {
      suggestions.push({
        field: key,
        suggestion: 'Add keywords to improve searchability',
        reason: 'Keywords make content easier to find'
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    suggestions
  };
}
